/* eslint-disable no-nested-ternary */
import React, { useRef, useState, useEffect } from "react";

import { Tooltip } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import ShipeeziNewGrid, {
  Column,
  GeneralActions,
  RowActions,
  RowActionsExtra,
} from "Components/AppGrid/shipeezi-new-grid";
import classNames from "classnames";
import { formatInTimeZone } from "date-fns-tz";
import { useTranslation } from "react-i18next";
import { FiActivity, FiEdit, FiEye } from "react-icons/fi";
import { HiOutlineMail } from "react-icons/hi";
import { useHistory } from "react-router-dom";

import { Color } from "../../../components/Form/Button";
import SideViewComponent, {
  Navigation,
  PropHandles,
} from "../../../components/Generic/ViewJobComponent";
import ContainerActivity from "../../../components/Shipments/Maintenance/Activity";
import SkeletonVoyageDetails from "../../../components/Shipments/Maintenance/VoyageDetails/skeleton";
import TitlePage from "../../../components/TitlePage";
import { VoyageDetails } from "Components/Tracking";
import { PRIVATE_ROUTES } from "../../../constants";
import { useAppState } from "../../../context/StateContext";
import useActionShipmentTracking from "../../../hooks/useActionShipmentTracking";
import { Customer } from "../../../models/customer";
import { ContainerPosition, ContainerStatus } from "../../../models/marine.traffic";
import { Filters, Searches, Sorts, Sort } from "../../../models/shipeezi.table";
import {
  ShipmentTracking as ConsolidateShipmentTrackingData,
  ShipmentTrackingToExport,
} from "../../../models/shipment.tracking";
import { Shipment } from "../../../models/shipments";
import { CustomerDetails, ShipmentsTracking } from "../../../models/shipments/shipments.tracking";
import SkeletonDetails from "../../../pages/private/Moving/Operations/Maintenance/skeleton";
import MarineTrafficService from "../../../services/marine.traffic.service";
import { ShipmentTrackingVersionService } from "../../../services/shipment.tracking.service";
import { calculateDelayedDays } from "../../../util/calculateDelayedDays";
import { ShipmentType, ShipmentStatus, ShipmentTrackingStatus } from "../../../util/enums";
import { exportCSV } from "../../../util/exportCsv";
import { getEmailAndPhoneFromCustomer } from "../../../util/getEmailAndPhoneFromCustomerArr";
import { getMeasurement } from "../../../util/getMeasurement";
import { handleSort } from "../../../util/handleSort";
import { shipmentStatus } from "../../../util/shipmentStatus";
import { uniqueArray } from "../../../util/uniqueArray";
import SendToCustomerModal, {
  SendToCustomerHandle,
} from "../Moving/Waybills/components/SendCommunicationsModal";
import { DataToShareLink } from "../Moving/Waybills/types";
import BadgeShipmentTrackingStatus from "./components/BadgeShipmentStatus";
import ShipmentTrackingDetailsView from "./Maintenance/Details/components/View";
import useStyles, { GenerateCSVButton } from "./styles";

export default function ShipmentTrackingScreen() {
  const { t: translate } = useTranslation();
  const history = useHistory();
  const classes = useStyles();
  const { currentUser } = useAppState();

  const sideViewRef = useRef<PropHandles>(null);
  const shareLinkModalRef = useRef<SendToCustomerHandle>(null);

  const [data, setData] = useState<ShipmentsTracking[]>([]);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [total, setTotal] = useState<number>(0);
  const [filters, setFilters] = useState<Filters>();
  const [searches, setSearches] = useState<Searches>([]);
  const [sorting, setSorting] = useState<Sorts>();

  const [selectedShipment, setSelectedShipment] = useState<ShipmentsTracking>();
  const [containerStatus, setContainerStatus] = useState<ContainerStatus>();
  const [containerPosition, setContainerPosition] = useState<ContainerPosition>();
  const [shipmentEmails, setShipmentEmails] = useState<string[]>([]);
  const [shipmentPhones, setShipmentPhones] = useState<string[]>([]);

  const measurement = getMeasurement();

  const [dataToExport, setDataToExport] = useState<ShipmentTrackingToExport[]>([]);

  const { getAllShipmentTracking, getShipmentTrackingCount } = useActionShipmentTracking();

  const { isFetching } = getAllShipmentTracking({
    options: {
      filters,
      searches,
      sorting,
      page,
      pageSize,
    },
    setShipments: (shipments) => {
      shipments.map(
        (shipment) => (shipment.statusShipment = shipment.shipmentTracking?.shipmentStatus)
      );

      setData(shipments);
    },
  });

  getShipmentTrackingCount({
    options: {
      filters,
      searches,
    },
    setTotal,
  });

  const hasUserAndCompanyAccessToPartnerField =
    !!currentUser?.companyId &&
    currentUser?.company?.allowAssociateAgent &&
    !!currentUser?.resources.find((resource) => resource.name === "Network");

  const handleCurrentPage = (pageNumber: number) => setPage(pageNumber - 1);

  const handleSearch = (value: Searches) => {
    setSearches(value);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    setFilters(value);
    setPage(0);
  };

  const handleEditButton = (_id: string | number, shipment: ShipmentsTracking) => {
    const isShipmentCompleted =
      shipment.shipmentTracking?.shipmentStatus === ShipmentStatus.COMPLETED;
    const isShipmentWithoutTracking =
      shipment.shipmentTracking?.shipmentStatus === ShipmentStatus.TRACKING_NOT_AVAILABLE;

    if (isShipmentCompleted) return null;

    if (isShipmentWithoutTracking) return null;

    return (
      <Tooltip title={translate("edit")} placement="top">
        <div>
          <FiEdit
            size={18}
            className={classes.button}
            color="#475467"
            data-cy="editButton"
            onClick={() => {
              history.push({
                pathname: `${PRIVATE_ROUTES.SHIPMENTS_TRACKING_MNT}/${shipment.id}`,
              });
            }}
          />
        </div>
      </Tooltip>
    );
  };

  const handleViewButton = (id: string | number) => {
    const shipment = data.find((data) => Number(id) === Number(data.id));

    const isShipmentWithoutTracking =
      shipment?.shipmentTracking?.shipmentStatus === ShipmentStatus.TRACKING_NOT_AVAILABLE;

    if (shipment?.shipmentTracking?.shipmentStatus === ShipmentTrackingStatus.WAITING_FOR_UPDATE)
      return null;

    if (!shipment?.shipmentTracking) return null;

    if (isShipmentWithoutTracking) return null;

    return (
      <Tooltip title={translate("view")} placement="top">
        <div>
          <FiEye
            size={18}
            className={classes.button}
            color="#475467"
            data-cy="viewButton"
            onClick={() => {
              setSelectedShipment(shipment);
              sideViewRef.current?.setOpenSideBar(true);
            }}
          />
        </div>
      </Tooltip>
    );
  };

  const returnEmailsAndPhoneNumbers = (shipment: ShipmentsTracking) => {
    const shipmentHasCustomers = !!shipment.customersToShipment?.length;
    const shipmentHasAgent = !!shipment.shipmentAgents?.length;

    let newEmails: string[] = [];
    let newPhones: string[] = [];

    const customers = shipment?.customersToShipment
      ?.map((customerToShipment) => customerToShipment.customer)
      ?.filter((customer) => customer !== undefined);
    const { emails, phones } = getEmailAndPhoneFromCustomer(customers as Customer[]);
    const agent = shipment.shipmentAgents?.[0]?.agent;
    const contactCommunication = shipment.communications?.filter(
      (communication) => !!communication.contactId
    );

    if (shipmentHasCustomers && shipmentHasAgent && hasUserAndCompanyAccessToPartnerField) {
      if (contactCommunication?.length) {
        const contact = contactCommunication?.[0]?.contact;

        if (contact) {
          newEmails = [...(emails ?? []), contact?.email];
          newPhones = [...(phones ?? []), contact?.mobilePhone];
        }
      } else if (agent) {
        newEmails = [...(emails ?? []), agent?.email];
        newPhones = [...(phones ?? []), agent?.phone];
      }
    } else if (shipmentHasCustomers) {
      if (emails) newEmails = emails;
      if (phones) newPhones = phones;
    } else if (shipmentHasAgent && hasUserAndCompanyAccessToPartnerField) {
      if (contactCommunication?.length) {
        const contact = contactCommunication?.[0]?.contact;

        if (contact) {
          newEmails = [contact?.email];
          newPhones = [contact?.mobilePhone];
        }
      } else if (agent) {
        newEmails = [agent?.email];
        newPhones = [agent?.phone];
      }
    }

    return { emails: newEmails, phones: newPhones };
  };

  const onSendShareLink = (data: DataToShareLink) => {
    shareLinkModalRef.current?.setShipmentId(data.id);
    shareLinkModalRef.current?.setContainerId(data.reference || "");
    setShipmentEmails(uniqueArray(data.emails));
    setShipmentPhones(uniqueArray(data.phones));
    shareLinkModalRef.current?.setOpen(true);
  };

  const handleEmailButton = (_id: string | number, shipment: ShipmentsTracking) => {
    const hasAgents = !!shipment?.shipmentAgents?.length && hasUserAndCompanyAccessToPartnerField;
    const hasCustomers = !!shipment?.customersToShipment?.length;

    const { emails: emailsToSend, phones: phonesToSend } = returnEmailsAndPhoneNumbers(shipment);

    const isShipmentWatingForUpdate =
      shipment?.shipmentTracking?.shipmentStatus === ShipmentStatus.WAITING_FOR_UPDATE;

    const isShipmentWithoutTracking =
      shipment?.shipmentTracking?.shipmentStatus === ShipmentStatus.TRACKING_NOT_AVAILABLE;

    const isShipmentCompleted =
      shipment?.shipmentTracking?.shipmentStatus === ShipmentStatus.COMPLETED;

    const isAbleToClickIt =
      shipment?.shipmentTrackingId && !isShipmentWatingForUpdate && (hasCustomers || hasAgents);

    if (!isAbleToClickIt) return null;

    if (isShipmentWithoutTracking) return null;

    if (isShipmentCompleted) return null;

    return (
      <Tooltip title={translate("communication")} placement="top">
        <div>
          <HiOutlineMail
            size={18}
            className={isAbleToClickIt ? classes.button : classes.disabledButton}
            color={!isAbleToClickIt ? "#98A2B3" : "#475467"}
            data-cy="sendEmailButton"
            onClick={() => {
              if (isAbleToClickIt && shipment?.id) {
                onSendShareLink({
                  id: shipment?.id,
                  emails: emailsToSend ?? [],
                  phones: phonesToSend ?? [],
                  reference: shipment?.containerId
                    ? `${translate("shipmentNumber")} ${shipment?.containerId || ""}`
                    : `${translate("containerNumber")} ${shipment?.containerNumbers || ""}`,
                });
              }
            }}
          />
        </div>
      </Tooltip>
    );
  };

  const getStyleForDelayText = (days?: number) => {
    if (days === undefined || days === null) return "";

    if (days >= 0 && days <= 3) return classes.lowDelayed;
    if (days > 3 && days <= 5) return classes.mediumDelayed;

    return classes.highDelayed;
  };

  const renderDelayDays = (shipmentTrackingConsolidate: ConsolidateShipmentTrackingData) => {
    if (!shipmentTrackingConsolidate) return null;

    const { finalPortPredictiveArrival, finalPortPredictiveArrivalLast } =
      shipmentTrackingConsolidate;

    const delayedDays = calculateDelayedDays(
      finalPortPredictiveArrival,
      finalPortPredictiveArrivalLast
    );

    return (
      delayedDays !== undefined && (
        <span className={classNames(classes.delayedDays, getStyleForDelayText(delayedDays))}>
          {delayedDays}{" "}
          {delayedDays !== undefined && delayedDays >= 1
            ? translate("days").toLowerCase()
            : translate("day").toLowerCase()}
        </span>
      )
    );
  };

  const { refetch, isFetching: fetchingDataToExport } = useQuery(
    ["export.csv"],
    () =>
      ShipmentTrackingVersionService.getDataToExport({
        companyId: currentUser?.companyId,
      }),
    {
      enabled: false,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setDataToExport(response.data);
      },
    }
  );

  const onExportToCsv = () => {
    return (
      <Tooltip title={translate("generateCsv")} placement="top">
        <div>
          <GenerateCSVButton
            loading={fetchingDataToExport}
            customColor={Color.SECONDARY_OUTLINED}
            onClick={() => refetch()}
          >
            {translate("generateCsv")}
          </GenerateCSVButton>
        </div>
      </Tooltip>
    );
  };

  useEffect(() => {
    if (dataToExport && dataToExport?.length) {
      exportCSV(dataToExport, measurement, currentUser?.company?.name, () => setDataToExport([]));
    }
  }, [dataToExport]);

  const columns: Column[] = [
    { name: "containerId", title: `${translate("shipment")} #`, searchIdentifier: "container_id" },
    { name: "shipmentType", title: translate("type"), hideSearch: true, hideSort: true },
    {
      name: "containerNumbers",
      title: translate("containerNumber"),
      searchIdentifier: "container_numbers",
      formatText: (containerNumber: string) => {
        if (containerNumber?.trim().length) return containerNumber.toUpperCase();

        return "";
      },
    },
    {
      name: "billOfLanding",
      title: translate("billOfLanding"),
      searchIdentifier: "bill_of_landing",
      formatText: (billOfLanding: string) => {
        if (billOfLanding?.trim().length) return billOfLanding.toUpperCase();

        return "";
      },
    },
    {
      name: "bookingNo",
      title: translate("bookingReference"),
      searchIdentifier: "booking_no",
      formatText: (bookingReference: string) => {
        if (bookingReference?.trim().length) return bookingReference.toUpperCase();

        return "";
      },
    },
    {
      name: "shipmentTracking",
      title: translate("origin"),
      formatText: (shipmentTracking: ConsolidateShipmentTrackingData) =>
        shipmentTracking ? shipmentTracking?.originPort ?? "" : "",
      hideSort: true,
      hideSearch: true,
    },
    {
      name: "shipmentTracking",
      title: translate("destination"),
      formatText: (shipmentTracking: ConsolidateShipmentTrackingData) =>
        shipmentTracking ? shipmentTracking?.finalPort ?? "" : "",
      hideSort: true,
      hideSearch: true,
    },
    {
      name: "customersToShipment",
      title: translate("customerName"),
      formatComponent: (customerDetails: CustomerDetails[]) =>
        customerDetails?.[0]?.customer?.fullName ?? "",
      hideSearch: true,
      hideSort: true,
    },
    {
      name: "customersToShipment",
      title: translate("customerEmail"),
      formatComponent: (customerDetails: CustomerDetails[]) =>
        customerDetails?.[0]?.customer?.email ?? "",
      hideSearch: true,
      hideSort: true,
    },
    {
      name: "customersToShipment",
      title: translate("phoneNumber"),
      formatComponent: (customerDetails: CustomerDetails[]) =>
        customerDetails?.[0]?.customer?.phone ?? "",
      hideSearch: true,
      hideSort: true,
    },
    {
      name: "shipmentTracking",
      title: translate("planned"),
      formatText: (shipmentTracking: ConsolidateShipmentTrackingData) =>
        shipmentTracking && shipmentTracking.finalPortPredictiveArrival
          ? formatInTimeZone(
              new Date(shipmentTracking?.finalPortPredictiveArrival),
              "UTC",
              "dd/MM/yyyy"
            )
          : "",
      hideSearch: true,
      hideSort: true,
      initialHideColumn: currentUser?.company?.configs?.isShipmentPlannedEtaAccessDisabled,
      hideShowColumn: currentUser?.company?.configs?.isShipmentPlannedEtaAccessDisabled,
    },
    {
      name: "shipmentTracking",
      title: translate("eta"),
      formatText: (shipmentTracking: ConsolidateShipmentTrackingData) =>
        shipmentTracking && shipmentTracking.finalPortPredictiveArrivalLast
          ? formatInTimeZone(
              new Date(shipmentTracking?.finalPortPredictiveArrivalLast) ?? "",
              "UTC",
              "dd/MM/yyyy"
            )
          : "",
      hideSearch: true,
      hideSort: true,
    },
    {
      name: "shipmentTracking",
      title: translate("delayed"),
      formatText: (shipmentTracking: ConsolidateShipmentTrackingData) =>
        renderDelayDays(shipmentTracking),
      initialHideColumn: currentUser?.company?.configs?.isShipmentPlannedEtaAccessDisabled,
      hideShowColumn: currentUser?.company?.configs?.isShipmentPlannedEtaAccessDisabled,
    },
    {
      name: "shipmentTracking",
      title: translate("status"),
      formatComponent: (shipmentTracking: ConsolidateShipmentTrackingData) => (
        <BadgeShipmentTrackingStatus
          value={shipmentStatus(shipmentTracking?.shipmentStatus) ?? translate("waitingForUpdate")}
        />
      ),
      hideSearch: true,
      hideSort: true,
    },
  ];

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "handleFilterBy"],
      right: ["handleExportToCsv"],
    },
    handleSearch: {
      onSearch: handleSearch,
    },
    handleFilterBy: {
      categories: [
        {
          name: translate("type"),
          column: "shipmentType",
          options: [{ value: ShipmentType.SEA, label: translate("sea") }],
        },
      ],
      onChange: handleFilter,
    },
    handleExportToCsv: onExportToCsv(),
  };

  const rowActions: RowActions = {
    orderBy: ["edit", "view", "email"],
  };

  const rowActionsExtra: RowActionsExtra[] = [
    { title: translate("edit"), key: "edit", buttonAndAction: handleEditButton },
    { title: translate("view"), key: "view", buttonAndAction: handleViewButton },
    { title: translate("email"), key: "email", buttonAndAction: handleEmailButton },
  ];

  const { isFetching: fetchingContainerStatus } = useQuery(
    [`container.status.${selectedShipment?.id}`],
    () =>
      MarineTrafficService.status(
        selectedShipment?.billOfLanding ||
          selectedShipment?.bookingNo ||
          selectedShipment?.containerNumbers ||
          selectedShipment?.airwayBill ||
          "",
        selectedShipment?.shipmentType,
        selectedShipment?.id
      ),
    {
      enabled:
        !!selectedShipment?.billOfLanding ||
        !!selectedShipment?.bookingNo ||
        !!selectedShipment?.containerNumbers ||
        !!selectedShipment?.airwayBill,
      refetchOnWindowFocus: false,
      cacheTime: 0,
      retry: false,
      onSuccess: (response) => {
        if (!response.data) setContainerStatus(undefined);
        if (response && response.data) {
          setContainerStatus(response.data);
          const position = {
            TIMESTAMP: selectedShipment?.shipmentTracking?.vesselPosition?.updatedAt,
            LAT: selectedShipment?.shipmentTracking?.vesselPosition?.latitude ?? "",
            LON: selectedShipment?.shipmentTracking?.vesselPosition?.longitude ?? "",
          } as unknown as ContainerPosition;

          setContainerPosition(position);
        }
      },
    }
  );

  const getSidebarTitle = (): string => {
    const customers = selectedShipment?.customersToShipment;

    if (customers && customers.length) {
      const firstCustomer = customers[0].customer;

      let fullName = `${firstCustomer?.name ?? ""} ${firstCustomer?.lastName ?? ""}`;

      if (firstCustomer?.fullName) fullName = firstCustomer.fullName;

      return `Shipment Tracking - ${fullName}`;
    }

    return `Shipment Tracking`;
  };

  const handleSetNavigation = () => {
    const navList = [{ key: Navigation.DETAILS }, { key: Navigation.VOYAGE_MAP }];

    return navList;
  };

  const handleSetTabs = () => {
    const tabs = [
      {
        key: Navigation.DETAILS,
        loading: false,
        isEmpty: !selectedShipment,
        render: () => <ShipmentTrackingDetailsView shipmentTracking={selectedShipment} />,
        renderLoading: () => <SkeletonDetails />,
      },
      {
        key: Navigation.VOYAGE_MAP,
        loading: fetchingContainerStatus,
        isEmpty: !containerStatus,
        render: () => (
          <VoyageDetails
            shipment={selectedShipment as Shipment}
            containerStatus={containerStatus}
            containerPosition={containerPosition}
            isOnViewComponent
            viewLivePosition={process.env.NODE_DATABASE !== "work"}
          />
        ),
        renderLoading: () => <SkeletonVoyageDetails />,
      },
    ];

    return tabs;
  };

  const handleSetButtonsComponent = () => {
    return [
      {
        buttonLabel: translate("seeActivity"),
        titlePage: translate("activity"),
        color: Color.SECONDARY,
        icon: <FiActivity size={16} />,
        renderLoading: () => <SkeletonDetails />,
        renderComponent: () => <ContainerActivity containerStatus={containerStatus} />,
      },
    ];
  };

  return (
    <main>
      {/* <TitlePage title={translate("shipments")} dataCy="shipmentTrackingList" /> */}
      <div className={classes.content}>
        <ShipeeziNewGrid
          data={data}
          totalItems={total}
          columns={columns}
          emptyData={translate("itFeelsABitEmpty")}
          loadingData={isFetching}
          currentPage={page}
          pageSize={pageSize}
          pageSizeList={[20, 40, 60, 100]}
          onChangePageSize={setPageSize}
          onNextPage={handleCurrentPage}
          onPreviousPage={handleCurrentPage}
          onGoToPage={handleCurrentPage}
          generalActions={generalActions}
          onSorting={(sorts: Sort[]) => setSorting(handleSort(sorts))}
          rowActions={rowActions}
          rowActionsExtra={rowActionsExtra}
          positionActionsColumn="right"
        />
      </div>

      <SendToCustomerModal
        ref={shareLinkModalRef}
        shipmentPhones={shipmentPhones}
        shipmentEmails={shipmentEmails}
      />

      <SideViewComponent
        ref={sideViewRef}
        isLoading={false}
        title={getSidebarTitle()}
        navigation={handleSetNavigation()}
        tabs={handleSetTabs()}
        buttonsComponent={handleSetButtonsComponent()}
      />
    </main>
  );
}
